/**
 * Country resolution, ported from `pop3d.countries`.
 *
 * The Python original leans on `pycountry`; the browser has no such package,
 * so the ISO 3166-1 table that ships with it is embedded verbatim below and
 * indexed the same way `pycountry.countries.lookup` indexes it.
 */

import type { Country } from "./types.ts";

/**
 * `alpha2|alpha3|name|officialName|commonName`, one country per line, in
 * pycountry's own order. Trailing fields are omitted when absent.
 */
const TABLE = `\
AW|ABW|Aruba
AF|AFG|Afghanistan|Islamic Republic of Afghanistan
AO|AGO|Angola|Republic of Angola
AI|AIA|Anguilla
AX|ALA|Åland Islands
AL|ALB|Albania|Republic of Albania
AD|AND|Andorra|Principality of Andorra
AE|ARE|United Arab Emirates
AR|ARG|Argentina|Argentine Republic
AM|ARM|Armenia|Republic of Armenia
AS|ASM|American Samoa
AQ|ATA|Antarctica
TF|ATF|French Southern Territories
AG|ATG|Antigua and Barbuda
AU|AUS|Australia
AT|AUT|Austria|Republic of Austria
AZ|AZE|Azerbaijan|Republic of Azerbaijan
BI|BDI|Burundi|Republic of Burundi
BE|BEL|Belgium|Kingdom of Belgium
BJ|BEN|Benin|Republic of Benin
BQ|BES|Bonaire, Sint Eustatius and Saba|Bonaire, Sint Eustatius and Saba
BF|BFA|Burkina Faso
BD|BGD|Bangladesh|People's Republic of Bangladesh
BG|BGR|Bulgaria|Republic of Bulgaria
BH|BHR|Bahrain|Kingdom of Bahrain
BS|BHS|Bahamas|Commonwealth of the Bahamas
BA|BIH|Bosnia and Herzegovina|Republic of Bosnia and Herzegovina
BL|BLM|Saint Barthélemy
BY|BLR|Belarus|Republic of Belarus
BZ|BLZ|Belize
BM|BMU|Bermuda
BO|BOL|Bolivia, Plurinational State of|Plurinational State of Bolivia|Bolivia
BR|BRA|Brazil|Federative Republic of Brazil
BB|BRB|Barbados
BN|BRN|Brunei Darussalam
BT|BTN|Bhutan|Kingdom of Bhutan
BV|BVT|Bouvet Island
BW|BWA|Botswana|Republic of Botswana
CF|CAF|Central African Republic
CA|CAN|Canada
CC|CCK|Cocos (Keeling) Islands
CH|CHE|Switzerland|Swiss Confederation
CL|CHL|Chile|Republic of Chile
CN|CHN|China|People's Republic of China
CI|CIV|Côte d'Ivoire|Republic of Côte d'Ivoire
CM|CMR|Cameroon|Republic of Cameroon
CD|COD|Congo, The Democratic Republic of the
CG|COG|Congo|Republic of the Congo
CK|COK|Cook Islands
CO|COL|Colombia|Republic of Colombia
KM|COM|Comoros|Union of the Comoros
CV|CPV|Cabo Verde|Republic of Cabo Verde
CR|CRI|Costa Rica|Republic of Costa Rica
CU|CUB|Cuba|Republic of Cuba
CW|CUW|Curaçao|Curaçao
CX|CXR|Christmas Island
KY|CYM|Cayman Islands
CY|CYP|Cyprus|Republic of Cyprus
CZ|CZE|Czechia|Czech Republic
DE|DEU|Germany|Federal Republic of Germany
DJ|DJI|Djibouti|Republic of Djibouti
DM|DMA|Dominica|Commonwealth of Dominica
DK|DNK|Denmark|Kingdom of Denmark
DO|DOM|Dominican Republic
DZ|DZA|Algeria|People's Democratic Republic of Algeria
EC|ECU|Ecuador|Republic of Ecuador
EG|EGY|Egypt|Arab Republic of Egypt
ER|ERI|Eritrea|the State of Eritrea
EH|ESH|Western Sahara
ES|ESP|Spain|Kingdom of Spain
EE|EST|Estonia|Republic of Estonia
ET|ETH|Ethiopia|Federal Democratic Republic of Ethiopia
FI|FIN|Finland|Republic of Finland
FJ|FJI|Fiji|Republic of Fiji
FK|FLK|Falkland Islands (Malvinas)
FR|FRA|France|French Republic
FO|FRO|Faroe Islands
FM|FSM|Micronesia, Federated States of|Federated States of Micronesia
GA|GAB|Gabon|Gabonese Republic
GB|GBR|United Kingdom|United Kingdom of Great Britain and Northern Ireland
GE|GEO|Georgia
GG|GGY|Guernsey
GH|GHA|Ghana|Republic of Ghana
GI|GIB|Gibraltar
GN|GIN|Guinea|Republic of Guinea
GP|GLP|Guadeloupe
GM|GMB|Gambia|Republic of the Gambia
GW|GNB|Guinea-Bissau|Republic of Guinea-Bissau
GQ|GNQ|Equatorial Guinea|Republic of Equatorial Guinea
GR|GRC|Greece|Hellenic Republic
GD|GRD|Grenada
GL|GRL|Greenland
GT|GTM|Guatemala|Republic of Guatemala
GF|GUF|French Guiana
GU|GUM|Guam
GY|GUY|Guyana|Republic of Guyana
HK|HKG|Hong Kong|Hong Kong Special Administrative Region of China
HM|HMD|Heard Island and McDonald Islands
HN|HND|Honduras|Republic of Honduras
HR|HRV|Croatia|Republic of Croatia
HT|HTI|Haiti|Republic of Haiti
HU|HUN|Hungary|Hungary
ID|IDN|Indonesia|Republic of Indonesia
IM|IMN|Isle of Man
IN|IND|India|Republic of India
IO|IOT|British Indian Ocean Territory
IE|IRL|Ireland
IR|IRN|Iran, Islamic Republic of|Islamic Republic of Iran|Iran
IQ|IRQ|Iraq|Republic of Iraq
IS|ISL|Iceland|Republic of Iceland
IL|ISR|Israel|State of Israel
IT|ITA|Italy|Italian Republic
JM|JAM|Jamaica
JE|JEY|Jersey
JO|JOR|Jordan|Hashemite Kingdom of Jordan
JP|JPN|Japan
KZ|KAZ|Kazakhstan|Republic of Kazakhstan
KE|KEN|Kenya|Republic of Kenya
KG|KGZ|Kyrgyzstan|Kyrgyz Republic
KH|KHM|Cambodia|Kingdom of Cambodia
KI|KIR|Kiribati|Republic of Kiribati
KN|KNA|Saint Kitts and Nevis
KR|KOR|Korea, Republic of||South Korea
KW|KWT|Kuwait|State of Kuwait
LA|LAO|Lao People's Democratic Republic||Laos
LB|LBN|Lebanon|Lebanese Republic
LR|LBR|Liberia|Republic of Liberia
LY|LBY|Libya|Libya
LC|LCA|Saint Lucia
LI|LIE|Liechtenstein|Principality of Liechtenstein
LK|LKA|Sri Lanka|Democratic Socialist Republic of Sri Lanka
LS|LSO|Lesotho|Kingdom of Lesotho
LT|LTU|Lithuania|Republic of Lithuania
LU|LUX|Luxembourg|Grand Duchy of Luxembourg
LV|LVA|Latvia|Republic of Latvia
MO|MAC|Macao|Macao Special Administrative Region of China
MF|MAF|Saint Martin (French part)
MA|MAR|Morocco|Kingdom of Morocco
MC|MCO|Monaco|Principality of Monaco
MD|MDA|Moldova, Republic of|Republic of Moldova|Moldova
MG|MDG|Madagascar|Republic of Madagascar
MV|MDV|Maldives|Republic of Maldives
MX|MEX|Mexico|United Mexican States
MH|MHL|Marshall Islands|Republic of the Marshall Islands
MK|MKD|North Macedonia|Republic of North Macedonia
ML|MLI|Mali|Republic of Mali
MT|MLT|Malta|Republic of Malta
MM|MMR|Myanmar|Republic of Myanmar
ME|MNE|Montenegro|Montenegro
MN|MNG|Mongolia
MP|MNP|Northern Mariana Islands|Commonwealth of the Northern Mariana Islands
MZ|MOZ|Mozambique|Republic of Mozambique
MR|MRT|Mauritania|Islamic Republic of Mauritania
MS|MSR|Montserrat
MQ|MTQ|Martinique
MU|MUS|Mauritius|Republic of Mauritius
MW|MWI|Malawi|Republic of Malawi
MY|MYS|Malaysia
YT|MYT|Mayotte
NA|NAM|Namibia|Republic of Namibia
NC|NCL|New Caledonia
NE|NER|Niger|Republic of the Niger
NF|NFK|Norfolk Island
NG|NGA|Nigeria|Federal Republic of Nigeria
NI|NIC|Nicaragua|Republic of Nicaragua
NU|NIU|Niue|Niue
NL|NLD|Netherlands|Kingdom of the Netherlands
NO|NOR|Norway|Kingdom of Norway
NP|NPL|Nepal|Federal Democratic Republic of Nepal
NR|NRU|Nauru|Republic of Nauru
NZ|NZL|New Zealand
OM|OMN|Oman|Sultanate of Oman
PK|PAK|Pakistan|Islamic Republic of Pakistan
PA|PAN|Panama|Republic of Panama
PN|PCN|Pitcairn
PE|PER|Peru|Republic of Peru
PH|PHL|Philippines|Republic of the Philippines
PW|PLW|Palau|Republic of Palau
PG|PNG|Papua New Guinea|Independent State of Papua New Guinea
PL|POL|Poland|Republic of Poland
PR|PRI|Puerto Rico
KP|PRK|Korea, Democratic People's Republic of|Democratic People's Republic of Korea|North Korea
PT|PRT|Portugal|Portuguese Republic
PY|PRY|Paraguay|Republic of Paraguay
PS|PSE|Palestine, State of|the State of Palestine
PF|PYF|French Polynesia
QA|QAT|Qatar|State of Qatar
RE|REU|Réunion
RO|ROU|Romania
RU|RUS|Russian Federation
RW|RWA|Rwanda|Rwandese Republic
SA|SAU|Saudi Arabia|Kingdom of Saudi Arabia
SD|SDN|Sudan|Republic of the Sudan
SN|SEN|Senegal|Republic of Senegal
SG|SGP|Singapore|Republic of Singapore
GS|SGS|South Georgia and the South Sandwich Islands
SH|SHN|Saint Helena, Ascension and Tristan da Cunha
SJ|SJM|Svalbard and Jan Mayen
SB|SLB|Solomon Islands
SL|SLE|Sierra Leone|Republic of Sierra Leone
SV|SLV|El Salvador|Republic of El Salvador
SM|SMR|San Marino|Republic of San Marino
SO|SOM|Somalia|Federal Republic of Somalia
PM|SPM|Saint Pierre and Miquelon
RS|SRB|Serbia|Republic of Serbia
SS|SSD|South Sudan|Republic of South Sudan
ST|STP|Sao Tome and Principe|Democratic Republic of Sao Tome and Principe
SR|SUR|Suriname|Republic of Suriname
SK|SVK|Slovakia|Slovak Republic
SI|SVN|Slovenia|Republic of Slovenia
SE|SWE|Sweden|Kingdom of Sweden
SZ|SWZ|Eswatini|Kingdom of Eswatini
SX|SXM|Sint Maarten (Dutch part)|Sint Maarten (Dutch part)
SC|SYC|Seychelles|Republic of Seychelles
SY|SYR|Syrian Arab Republic||Syria
TC|TCA|Turks and Caicos Islands
TD|TCD|Chad|Republic of Chad
TG|TGO|Togo|Togolese Republic
TH|THA|Thailand|Kingdom of Thailand
TJ|TJK|Tajikistan|Republic of Tajikistan
TK|TKL|Tokelau
TM|TKM|Turkmenistan
TL|TLS|Timor-Leste|Democratic Republic of Timor-Leste
TO|TON|Tonga|Kingdom of Tonga
TT|TTO|Trinidad and Tobago|Republic of Trinidad and Tobago
TN|TUN|Tunisia|Republic of Tunisia
TR|TUR|Türkiye|Republic of Türkiye
TV|TUV|Tuvalu
TW|TWN|Taiwan, Province of China|Taiwan, Province of China|Taiwan
TZ|TZA|Tanzania, United Republic of|United Republic of Tanzania|Tanzania
UG|UGA|Uganda|Republic of Uganda
UA|UKR|Ukraine
UM|UMI|United States Minor Outlying Islands
UY|URY|Uruguay|Eastern Republic of Uruguay
US|USA|United States|United States of America
UZ|UZB|Uzbekistan|Republic of Uzbekistan
VA|VAT|Holy See (Vatican City State)
VC|VCT|Saint Vincent and the Grenadines
VE|VEN|Venezuela, Bolivarian Republic of|Bolivarian Republic of Venezuela|Venezuela
VG|VGB|Virgin Islands, British|British Virgin Islands
VI|VIR|Virgin Islands, U.S.|Virgin Islands of the United States
VN|VNM|Viet Nam|Socialist Republic of Viet Nam|Vietnam
VU|VUT|Vanuatu|Republic of Vanuatu
WF|WLF|Wallis and Futuna
WS|WSM|Samoa|Independent State of Samoa
YE|YEM|Yemen|Republic of Yemen
ZA|ZAF|South Africa|Republic of South Africa
ZM|ZMB|Zambia|Republic of Zambia
ZW|ZWE|Zimbabwe|Republic of Zimbabwe`;

/** Common names and short forms `pycountry` does not know. */
const ALIASES: Readonly<Record<string, string>> = {
  bolivia: "BOL",
  brunei: "BRN",
  "cape verde": "CPV",
  "czech republic": "CZE",
  czechia: "CZE",
  "democratic republic of the congo": "COD",
  "dr congo": "COD",
  england: "GBR",
  iran: "IRN",
  "ivory coast": "CIV",
  laos: "LAO",
  moldova: "MDA",
  "north korea": "PRK",
  palestine: "PSE",
  russia: "RUS",
  "south korea": "KOR",
  syria: "SYR",
  taiwan: "TWN",
  tanzania: "TZA",
  "the netherlands": "NLD",
  turkey: "TUR",
  turkiye: "TUR",
  uk: "GBR",
  "u k": "GBR",
  "united states": "USA",
  usa: "USA",
  us: "USA",
  venezuela: "VEN",
  vietnam: "VNM",
};

/** Fold accents away and reduce every run of punctuation to a single space. */
function normalise(value: string): string {
  return value
    .normalize("NFKD")
    .replace(/\p{M}+/gu, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function stripAccents(value: string): string {
  return value.normalize("NFKD").replace(/\p{M}+/gu, "");
}

interface Entry {
  country: Country;
  /** Name and official name, lowercased and unaccented, for fuzzy scoring. */
  searchable: string[];
  /** Upper-case initials of each searchable name, e.g. `usa`. */
  initials: string[];
}

const ENTRIES: Entry[] = [];
const BY_ALPHA2 = new Map<string, Country>();
const BY_ALPHA3 = new Map<string, Country>();
const BY_NAME = new Map<string, Country>();

for (const line of TABLE.split("\n")) {
  const [iso2 = "", iso3 = "", name = "", official = "", common = ""] = line.split("|");
  const display = common || name;
  const country: Country = {
    name: display,
    iso2,
    iso3,
    slug: normalise(display).replace(/ /g, "-"),
  };
  const named = [name, official, common].filter((value) => value !== "");
  ENTRIES.push({
    country,
    searchable: named.map((value) => stripAccents(value).toLowerCase()),
    initials: named.map((value) =>
      stripAccents(value)
        .replace(/[^A-Z]/g, "")
        .toLowerCase(),
    ),
  });
  BY_ALPHA2.set(iso2, country);
  BY_ALPHA3.set(iso3, country);
  for (const value of named) {
    BY_NAME.set(normalise(value), country);
  }
}

/**
 * Stand-in for `pycountry.search_fuzzy`: the subdivision database it also
 * consults is far too heavy for the client, so only the country-name passes
 * survive, with the same scoring.
 */
function searchFuzzy(value: string): Country | undefined {
  const query = stripAccents(value.trim().toLowerCase());
  if (query === "") {
    return undefined;
  }
  let best: Country | undefined;
  let bestPoints = 0;
  for (const entry of ENTRIES) {
    let points = 0;
    for (let index = 0; index < entry.searchable.length; index += 1) {
      if (query === entry.initials[index]) {
        points = 40;
        break;
      }
      const found = entry.searchable[index].indexOf(query);
      if (found >= 0) {
        points = Math.max(5, 30 - 2 * found);
        break;
      }
    }
    if (points > bestPoints) {
      best = entry.country;
      bestPoints = points;
    }
  }
  return best;
}

/** Resolve a common name, alias, alpha-2 or alpha-3 ISO code. */
export function resolveCountry(query: string): Country {
  const normalised = normalise(query);
  if (normalised === "") {
    throw new Error("Il nome dello Stato non pu\u00f2 essere vuoto.");
  }

  const aliasIso3 = ALIASES[normalised];
  let match: Country | undefined;
  if (aliasIso3 !== undefined) {
    match = BY_ALPHA3.get(aliasIso3);
  } else if (normalised.length === 2) {
    match = BY_ALPHA2.get(normalised.toUpperCase());
  } else if (normalised.length === 3) {
    match = BY_ALPHA3.get(normalised.toUpperCase());
  } else {
    match = BY_NAME.get(normalised) ?? searchFuzzy(query);
  }

  if (match === undefined) {
    throw new Error(
      `Stato non riconosciuto: '${query}'. Usa un nome comune o un codice ISO ` +
        "alpha-2/alpha-3 (per esempio italy, IT o ITA).",
    );
  }
  return match;
}
